'use client';

import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export const breakpoints = {
  xs: 360, // matches Tailwind’s “xs: 360px”
  sm: 768, // matches Tailwind’s “sm: 768px”
  md: 1200, // matches your Tailwind “md:” (1200px)
  lg: 1440, // matches “lg:” (1440px)
  xl: 1920, // matches “xl:” (1920px)
} as const;

export type Breakpoint = 'base' | keyof typeof breakpoints;

function useBreakpointInner(): Breakpoint {
  // initialize to “base” for SSR or narrow view
  const [bp, setBp] = useState<Breakpoint>('md');

  useEffect(() => {
    const mqlEntries = (Object.entries(breakpoints) as [Exclude<Breakpoint, 'base'>, number][]).map(
      ([label, min]) => [label, window.matchMedia(`(min-width: ${min}px)`)] as const,
    );

    const handler = () => {
      const match = mqlEntries
        .filter(([, mql]) => mql.matches)
        .map(([label]) => label)
        .pop(); // last = largest one
      setBp(match ?? 'base');
    };

    // add listeners
    mqlEntries.forEach(([, mql]) => mql.addEventListener('change', handler));
    // run once on mount
    handler();

    return () => {
      mqlEntries.forEach(([, mql]) => mql.removeEventListener('change', handler));
    };
  }, []);

  return bp;
}

const BreakpointContext = createContext<Breakpoint>('base');

export function BreakpointProvider({ children }: { children: ReactNode }) {
  const bp = useBreakpointInner();
  return <BreakpointContext.Provider value={bp}>{children}</BreakpointContext.Provider>;
}

export function useBreakpoint() {
  const breakpoint = useContext(BreakpointContext);
  return useMemo(() => {
    return breakpoint;
  }, [breakpoint]);
}
